import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { TempDocumentDetailResponseWithAny } from "features/document/hooks/queries/useTempDocumentDetailQuery";
import { useRecoilValue, useSetRecoilState } from "recoil";
import {
  selectedWorkDocumentClassState,
  workFormStepState,
} from "store/document/workDocument";
import { useNavigate, useParams } from "react-router-dom";
import { DOCUMENT_CLASS_UUID, DOCUMENT_UUID } from "config/constant";
import CreateDocumentForm from "components/document/Create/CreateDocumentForm/CreateDocumentForm";
import {
  DocumentAttachmentFileRequest,
  TempDocumentDetailResponse,
} from "apis/data-contracts";
import LeaveApplicationForm from "components/document/Create/CreateDocumentForm/docClass/work/LeaveApplicationForm";
import ReceiveOfficialLetter from "components/document/Create/CreateDocumentForm/docClass/work/ReceiveOfficialLetter";
import SendOfficialLetter from "components/document/Create/CreateDocumentForm/docClass/work/SendOfficialLetter";
import LetterOfApproval from "components/document/Create/CreateDocumentForm/docClass/work/LetterOfApproval";
import ExpenseReport from "components/document/Create/CreateDocumentForm/docClass/work/ExpenseReport";
import MinutesOfMeeting from "components/document/Create/CreateDocumentForm/docClass/work/MinutesOfMeeting";
import Report from "components/document/Create/CreateDocumentForm/docClass/work/Report";
import KSS from "components/document/Create/CreateDocumentForm/docClass/work/KSS";
import CorpCardUsageProof from "components/document/Create/CreateDocumentForm/docClass/work/CorpCardUsageProof";
import Button from "components/foundation/Button";
import { ROUTER_PATH } from "config/path";
import Toast, { useToast } from "components/foundation/Toast";
import Modal from "components/foundation/Modal/Modal";
import { FormProvider, useFormContext } from "react-hook-form";
import {
  DECISION_MANAGER_FIELD_NAME,
  REFERENCE_MEMBER_FIELD_NAME,
} from "config/documentConstant";
import BeforeUnloadModal from "components/Common/BeforeUnloadModal";
import useApproveRequest from "features/document/hooks/create/useApproveRequest";
import useDocumentTempSave from "features/document/hooks/create/useDocumentTempSave";
import useInitWorkDocTags from "features/document/hooks/create/useInitWorkDocTags";
import Divider from "components/foundation/Divider";
import useMediaView from "features/common/hooks/useMediaView";
import getTempSaveRequest, {
  DocumentForm,
} from "features/document/utils/getTempSaveRequest";
import useWorkDocumentTags from "features/document/hooks/create/useWorkDocumentTags";
import useHomeButton from "features/document/hooks/create/useHomeButton";
import useRemoveTempDoc from "features/document/hooks/create/useRemoveTempDoc";
import useFreeTags from "features/document/hooks/create/useFreeTags";
import useWorkSelectFormStates from "features/document/hooks/create/useWorkSelectFormStates";
import useReselectDoc from "features/document/hooks/create/useReselectDoc";
import { isFormReselectedState } from "store/document/documentCommon";

interface WorkCreateFormPageProps {
  tempDocumentDetail?: TempDocumentDetailResponseWithAny;
}

const WorkCreateFormPage = ({
  tempDocumentDetail,
}: WorkCreateFormPageProps) => {
  const tempDocumentUuid = useParams().uuid;
  const isSmallView = useMediaView("small");

  const navigate = useNavigate();

  /* 데이터: 문서 템플릿 */
  const documentClass = useRecoilValue(selectedWorkDocumentClassState);
  const documentClassUuid =
    documentClass?.uuid || (tempDocumentDetail?.documentClassUuid as string);

  /* 폼 */
  const { methods, documentClassName } = useWorkCreateForm({
    tempDocumentDetail,
    documentClassUuid,
  });

  /* BeforeUnload 비활성화 */
  const [beforeUnloadDisable, setBeforeUnloadDisable] = useState(false);

  /* 태그 */
  const { companyTag, topicTags, companyAndPjTags, getTagUuids } =
    useWorkDocumentTags();

  const { freeTags, getFreeTagUuids } = useFreeTags({ tempDocumentDetail });

  // 문서 태그 초기화
  useInitWorkDocTags({
    tempDocumentUuid,
    documentClassUuid,
  });

  const { onClickReselectWorkDoc } = useReselectWorkDoc({ tempDocumentUuid });

  /* 임시 저장 */
  const { openTempSaveToast, onOpenChangeTempSaveToast, onTempSaveDoc } =
    useDocumentTempSave({
      tempDocumentDetail,
      documentClassName,
      documentClassUuid,
      companyUuid: companyTag?.uuid,
      getTagUuids,
      setBeforeUnloadDisable,
    });

  /* Home 버튼 */
  const { openHomeModal, setOpenHomeModal, onClickHomeButton, goToHome } =
    useHomeButton({
      setBeforeUnloadDisable,
    });

  /* 임시 저장 삭제 */
  const {
    openRemoveTempDocToast,
    onOpenChangeRemoveTempDocToast,

    onClickRemoveTempDoc,
  } = useRemoveTempDoc({ tempDocumentUuid, setBeforeUnloadDisable });

  /* 결재요청 */
  const approveButtonLabel =
    tempDocumentDetail?.documentApprove?.approveStatus !== "REQ_SUPPLEMENT"
      ? "결재요청"
      : "수정 후 재요청";

  const {
    openRequestApprovalToast,
    onOpenChangeRequestApprovalToast,

    onClickRequestApproval,
  } = useWorkDocApproval({
    methods,
    tempDocumentDetail,
    setBeforeUnloadDisable,

    documentClassUuid,
    documentClassName,
    companyUuid: companyTag?.uuid,

    getTagUuids,
    getFreeTagUuids,
  });

  /* 모바일 : 문서 종류 접근 제한 */
  if (
    isSmallView &&
    documentClassUuid !== DOCUMENT_UUID.WORK.classes.휴가신청서.uuid
  ) {
    navigate(-1);
    return;
  }

  return (
    <>
      <FormProvider {...methods}>
        <WrapStyle>
          <CreateDocumentForm.Header
            title={documentClassName}
            tempDocumentUuid={tempDocumentUuid}
            docTags={{
              "법인 / PJ명": companyAndPjTags.map((tag) => tag.tagName),
              주제분류: topicTags.map((tag) => tag.tagName),
            }}
            freeTags={freeTags}
            actions={
              <>
                <Button
                  className="pc-only"
                  variant="tertiary"
                  onClick={onClickReselectWorkDoc}
                >
                  재선택
                </Button>

                <Button
                  className="mobile-only"
                  variant="tertiary"
                  size="small"
                  onClick={onClickReselectWorkDoc}
                >
                  재선택
                </Button>
              </>
            }
          />

          <Divider className="mobile-only" />

          <CreateDocumentForm.Body
            tempDocumentUuid={tempDocumentUuid}
            documentClassUuid={documentClassUuid}
            actions={
              isSmallView ? (
                <Button size="large" fill onClick={onClickRequestApproval}>
                  {approveButtonLabel}
                </Button>
              ) : (
                <>
                  <Button
                    size="large"
                    variant="tertiary"
                    onClick={onClickHomeButton}
                  >
                    HOME
                  </Button>
                  {tempDocumentUuid && (
                    <Button
                      size="large"
                      variant="tertiary-neg"
                      onClick={onClickRemoveTempDoc}
                    >
                      임시저장 삭제
                    </Button>
                  )}
                  <Button size="large" onClick={onClickRequestApproval}>
                    {approveButtonLabel}
                  </Button>
                </>
              )
            }
          >
            <Form
              tempDocumentDetail={tempDocumentDetail}
              documentClassUuid={documentClassUuid}
            />
          </CreateDocumentForm.Body>
        </WrapStyle>
      </FormProvider>

      <Modal.Root open={openHomeModal} onOpenChange={setOpenHomeModal}>
        <Modal.Body
          title="작성한 문서를 임시저장 하시겠습니까?"
          onCancel={goToHome}
          cancelValue="나가기"
          onConfirm={() => onTempSaveDoc({ form: methods.watch() })}
          confirmValue="임시저장"
        >
          {"'나가기' 클릭 시 작성한 문서는 저장되지 않습니다."}
        </Modal.Body>
      </Modal.Root>

      {isSmallView ? (
        <BeforeUnloadModal
          isDirty={methods.formState.isDirty}
          disable={beforeUnloadDisable}
          onConfirm={() => onTempSaveDoc({ form: methods.watch() })}
          confirmValue="임시저장"
        />
      ) : (
        <BeforeUnloadModal
          isDirty={methods.formState.isDirty}
          disable={beforeUnloadDisable}
        />
      )}

      <Toast.Message
        open={openTempSaveToast}
        onOpenChange={onOpenChangeTempSaveToast}
      >
        해당 문서가 임시저장 되었습니다.
      </Toast.Message>

      <Toast.Message
        open={openRemoveTempDocToast}
        onOpenChange={onOpenChangeRemoveTempDocToast}
      >
        해당 임시 문서가 삭제되었습니다.
      </Toast.Message>

      <Toast.Message
        open={openRequestApprovalToast}
        onOpenChange={onOpenChangeRequestApprovalToast}
      >
        해당 문서가 결재 요청되었습니다.
      </Toast.Message>
    </>
  );
};

export default WorkCreateFormPage;

interface FormProps {
  tempDocumentDetail?: TempDocumentDetailResponse;
  documentClassUuid?: string;
}

const Form = ({ documentClassUuid, tempDocumentDetail }: FormProps) => {
  switch (documentClassUuid) {
    case DOCUMENT_UUID.WORK.classes.휴가신청서.uuid:
      return <LeaveApplicationForm tempDocumentDetail={tempDocumentDetail} />;
    case DOCUMENT_UUID.WORK.classes["공문 수신"].uuid:
      return <ReceiveOfficialLetter tempDocumentDetail={tempDocumentDetail} />;
    case DOCUMENT_UUID.WORK.classes["공문 발신"].uuid:
      return <SendOfficialLetter tempDocumentDetail={tempDocumentDetail} />;
    case DOCUMENT_UUID.WORK.classes.품의서.uuid:
      return <LetterOfApproval tempDocumentDetail={tempDocumentDetail} />;
    case DOCUMENT_UUID.WORK.classes.지출결의서.uuid:
      return <ExpenseReport tempDocumentDetail={tempDocumentDetail} />;
    case DOCUMENT_UUID.WORK.classes.회의록.uuid:
      return <MinutesOfMeeting tempDocumentDetail={tempDocumentDetail} />;
    case DOCUMENT_UUID.WORK.classes.보고서.uuid:
      return <Report tempDocumentDetail={tempDocumentDetail} />;
    case DOCUMENT_UUID.WORK.classes.KSS.uuid:
      return <KSS tempDocumentDetail={tempDocumentDetail} />;
    case DOCUMENT_UUID.WORK.classes["법인카드 사용 증빙"].uuid:
      return <CorpCardUsageProof tempDocumentDetail={tempDocumentDetail} />;
  }
  return;
};

/* Hooks */
const useWorkCreateForm = ({
  tempDocumentDetail,
  documentClassUuid,
}: {
  tempDocumentDetail?: TempDocumentDetailResponseWithAny;
  documentClassUuid: string;
}) => {
  const methods = useFormContext<DocumentForm>();

  const documentClassName =
    DOCUMENT_CLASS_UUID[documentClassUuid as keyof typeof DOCUMENT_CLASS_UUID];

  const { setWorkSelectStatesUsingTempDoc } = useWorkSelectFormStates();
  const isFormReselected = useRecoilValue(isFormReselectedState);

  // 폼 기본 값 세팅
  useEffect(() => {
    if (isFormReselected) return;
    if (!tempDocumentDetail) return;

    setWorkSelectStatesUsingTempDoc(tempDocumentDetail);

    const existingAttachments: DocumentAttachmentFileRequest[] =
      tempDocumentDetail.attachments.map((attachment) => ({
        fileName: attachment.fileName,
        fileHash: attachment.md5Hash,
        documentFileUrl: attachment.fileUrl,
      }));
    methods.reset({
      ...tempDocumentDetail.documentData,
      attachmentFiles: existingAttachments,
    });
  }, [isFormReselected, tempDocumentDetail]);

  return {
    methods,
    documentClassName,
  };
};

const useReselectWorkDoc = ({
  tempDocumentUuid,
}: {
  tempDocumentUuid?: string;
}) => {
  const setWorkFormStep = useSetRecoilState(workFormStepState);

  const { onClickReselect } = useReselectDoc();

  const onClickReselectWorkDoc = () =>
    onClickReselect({
      uuid: tempDocumentUuid,
      callback: () => {
        setWorkFormStep(0);
      },
    });

  return {
    onClickReselectWorkDoc,
  };
};

const useWorkDocApproval = ({
  methods,
  tempDocumentDetail,
  setBeforeUnloadDisable,

  documentClassUuid,
  documentClassName,
  companyUuid,

  getTagUuids,
  getFreeTagUuids,
}: {
  methods: any;
  tempDocumentDetail?: TempDocumentDetailResponseWithAny;
  setBeforeUnloadDisable: (disable: boolean) => void;

  documentClassUuid: string;
  documentClassName: string;
  companyUuid?: string;

  getTagUuids: () => string[];
  getFreeTagUuids: () => string[];
}) => {
  const navigate = useNavigate();

  // 결재요청 토스트
  const {
    open: openRequestApprovalToast,
    onOpenToast: onOpenRequestApprovalToast,
    onOpenChange: onOpenChangeRequestApprovalToast,
  } = useToast();

  const { saveAndApproveRequest, updateAndApproveRequest } =
    useApproveRequest();

  /**
   * @description 결재요청 버튼 클릭 이벤트 핸들러
   */
  const onClickRequestApproval = methods.handleSubmit(
    async (form: DocumentForm) => {
      // 결재 승인 요청 정보 취합
      const approverUuid = form[DECISION_MANAGER_FIELD_NAME];
      const referenceUserUuid = form[REFERENCE_MEMBER_FIELD_NAME];
      const approvalRequestProps = {
        approverUuids: approverUuid ? [approverUuid] : undefined,
        referenceUserUuids: referenceUserUuid ? referenceUserUuid : undefined,
        successCallback() {
          onOpenRequestApprovalToast(() => {
            setBeforeUnloadDisable(true);
            navigate(ROUTER_PATH.home);
          });
        },
      };

      if (!tempDocumentDetail) {
        // 저장된 적 없는 문서는 임시 저장 후 결재 승인 요청
        const saveRequest = getTempSaveRequest({
          documentClassUuid,
          documentClassName,
          form,
          tagUuids: getTagUuids(),
          freeTagUuids: getFreeTagUuids(),
          companyUuid,
        });

        saveAndApproveRequest({
          saveTempDocProps: {
            saveRequest,
          },
          approvalRequestProps,
        });
      } else {
        // 저장된 적 있는 문서는 업데이트 후 결재 승인 요청
        // 업데이트 & 승인 요청
        updateAndApproveRequest({
          updateRequestProps: {
            tempDocumentDetail,
            form,
            tagUuids: getTagUuids(),
            freeTagUuids: getFreeTagUuids(),
            companyUuid,
          },
          approvalRequestProps,
        });
      }
    },
  );

  return {
    openRequestApprovalToast,
    onOpenChangeRequestApprovalToast,

    onClickRequestApproval,
  };
};

/* Styles */
export const WrapStyle = styled.div`
  width: 100.6rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;

  ${({ theme }) => theme.media.small} {
    width: 100%;
    padding: 2rem 1.6rem;
    background-color: ${({ theme }) => theme.color.background.white};
  }
`;
